if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.enableSlider = this.dataset.enableSlider === 'true';
        this.selectedIndex = this.selectedIndex;
        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.containerWidth = this.dataset.container;
        this.cleanupScrollAnimations = [];
        this.enableZoom = this.dataset.enableZoom ? this.dataset.enableZoom === 'true' : true;
        this.borderRadius = this.dataset.borderRadius;
        this.zoom = 0;
        this.useCustomPagination = this.dataset.useCustomPagination === 'true';
        this.navigationPosition = this.dataset.navigationPosition || 'bottom';
        this.loop = this.dataset.loop === 'true';
        this.paginationInitialized = false;
        this.paginationScrollRAF = null;
        this.scrollTimeout = null;
        
        if (!this.enableSlider) return;

        this.setupZoom();
        window.addEventListener('resize', this.setupZoom.bind(this));

        this.initSlider();
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      setupZoom() {
        if (!this.enableZoom) return;

        this.destroyZoom();

        const pageWidthEl = this.querySelector('.page-width');
        const paddingLeft = window.getComputedStyle(pageWidthEl).getPropertyValue('padding-left');
        this.zoom = Math.min(parseFloat(paddingLeft.replace('px', '')), (window.innerWidth * 0.04) );

        if (this.zoom > 15) {
          this.initZoom();
        }
      }

      initSlider() {
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: this.loop,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
          effect: 'fade',
          on: {
            init: this.handleAfterInit.bind(this),
            paginationRender: this.handlePaginationRender.bind(this),
            paginationUpdate: this.handlePaginationRender.bind(this),
          },
          fadeEffect: {
            crossFade: true,
          },
        };

        if (this.sliderControls) {
          this.sliderOptions.navigation = {
            nextEl: this.sliderControls.querySelector('.swiper-button-next'),
            prevEl: this.sliderControls.querySelector('.swiper-button-prev'),
          };
          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector('.swiper-pagination'),
            clickable: !this.useCustomPagination,
            type: 'bullets',
          };

          // 如果启用自定义分页，则从已渲染的占位 bullet 中提取完整结构（含内部按钮）并使用 renderBullet 渲染
          if (this.useCustomPagination) {
            const presetBullets = Array.from(
              this.sliderControls.querySelectorAll('.swiper-pagination .swiper-pagination-bullet')
            );
            const customBulletHTML = presetBullets.map((el) => (el.innerHTML || '').trim());
            const customBulletLabels = presetBullets.map((el) => (el.getAttribute('data-label') || '').trim());

            const escapeAttr = (str) => str
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/\"/g, '&quot;')
              .replace(/'/g, '&#39;');

            this.sliderOptions.pagination.renderBullet = (index, className) => {
              const labelRaw = customBulletLabels[index] || `${index + 1}`;
              const labelEscaped = escapeAttr(labelRaw);
              const inner = customBulletHTML[index] || labelEscaped;
              return `<div class="${className}" data-label="${labelEscaped}" data-index="${index}">${inner}</div>`;
            };
          }
        }

        const autoplayDelay = parseInt(this.dataset.autoplay);
        if (autoplayDelay > 0) {
          this.sliderOptions = {
            ...this.sliderOptions,
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false,
            },
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();

        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));

        if (this.sliderInstance) {
          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement, this.sliderInstance.slider.slides);

          // 为自定义 pagination 添加点击处理
          if (this.useCustomPagination && this.sliderControls) {
            const paginationEl = this.sliderControls.querySelector('.swiper-pagination');
            if (paginationEl) {
              paginationEl.addEventListener('click', (e) => {
                const bullet = e.target.closest('.swiper-pagination-bullet');
                if (bullet && !bullet.classList.contains('swiper-pagination-bullet-active')) {
                  const index = parseInt(bullet.dataset.index);
                  if (!isNaN(index)) {
                    if (this.loop) {
                      this.sliderInstance.slider.slideToLoop(index);
                    } else {
                      this.sliderInstance.slider.slideTo(index);
                    }
                  }
                }
              });
            }
          }

          // Fix accessibility
          const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          focusableElements.forEach((element) => {
            if (!element.classList.contains('swiper-button')) {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                const slideIndex = this.sliderInstance.slider.slides.indexOf(slide);
                if (this.loop) {
                  this.sliderInstance.slider.slideToLoop(slideIndex);
                } else {
                  this.sliderInstance.slider.slideTo(slideIndex);
                }
              });
            }
          });
        }

        this.calcNavButtonsPosition();
      }

      onReady(selectedElement) {
        if (selectedElement.dataset.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'));
          videoElement?.play();
        }

        if (!FoxTheme.config.motionReduced) {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            setTimeout(() => {
              motionEl && motionEl.refreshAnimation();
            });
          });
        }
      }

      handleAfterInit() {
        this.removeAttribute('data-media-loading');

        // Fix active bullet not transition on the first time.
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');

          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow.
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      handlePaginationRender() {
        // Pagination 渲染完成后，检查是否需要滚动并滚动到 active bullet
        if (!this.useCustomPagination) return;

        setTimeout(() => {
          this.checkAndSetScrollable();
          // 只在初始化时滚动，不在每次渲染时都滚动
          if (!this.paginationInitialized && this.sliderInstance && this.sliderInstance.slider) {
            this.scrollToActivePagination(this.sliderInstance.slider.realIndex);
            this.paginationInitialized = true;
          }
        }, 100);
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;

        this.updateControlsScheme(slides[activeIndex]);

        // 使用 requestAnimationFrame 确保在 Swiper 动画完成后再滚动
        if (this.paginationScrollRAF) {
          cancelAnimationFrame(this.paginationScrollRAF);
        }
        
        this.paginationScrollRAF = requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.scrollToActivePagination(realIndex);
          });
        });
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([fromElement.querySelector('video-element')]);
              videoElement && videoElement.pause();
            }

            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls &&
              motionEls.forEach((motionEl) => {
                if (motionEl.hasAttribute('data-text')) {
                  motionEl.resetAnimation();
                }
              });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                if (toElement.dataset.type === 'video') {
                  const videoElement = FoxTheme.utils.displayedMedia([toElement.querySelector('video-element')]);
                  videoElement && videoElement.play();
                }

                const motionEls = toElement.querySelectorAll('motion-element');
                motionEls.forEach((motionEl) => {
                  setTimeout(() => {
                    motionEl && motionEl.refreshAnimation();
                  });
                });
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      initZoom() {
        if (this.containerWidth !== 'full') return;

        const motionTarget = {
          target: this.section,
          offset: [
            [-0.1, 0],
            [0.7, 0],
          ],
        };

        this.querySelectorAll('.slideshow__item-wrap').forEach((slide) => {
          const slideBg = slide.querySelector('.slideshow__bg');

          this.cleanupScrollAnimations.push(
            FoxTheme.Motion.scroll(
              FoxTheme.Motion.animate(slideBg, {
                transform: [`scale(1) translateZ(0)`, `scale(1.1) translateZ(0)`],
              }),
              motionTarget
            )
          );
        });

        this.cleanupScrollAnimations.push(
          FoxTheme.Motion.scroll(
            FoxTheme.Motion.animate(this, {
              clipPath: [
                `inset(0%)`,
                `inset(${Math.min(50, this.zoom)}px ${this.zoom}px round ${this.borderRadius}px)`,
              ],
            }),
            motionTarget
          )
        );
        
        if (this.sliderControls) {
          this.cleanupScrollAnimations.push(
            FoxTheme.Motion.scroll(
              FoxTheme.Motion.animate(this.sliderControls, {
                bottom: [`0`, `${Math.min(50, this.zoom)}px`],
              }),
              motionTarget
            )
          );
        }
      }

      calcNavButtonsPosition() {
        if (this.navigationPosition !== 'middle') return;

        const firstSlideImage = this.querySelector('.slideshow__item .slideshow__media');
        const controls = this.sliderControls;
        if (controls && firstSlideImage && firstSlideImage.clientHeight > 0) {
          controls.style.setProperty('--swiper-navigation-top-offset', parseInt(firstSlideImage.clientHeight) / 2 + 'px');
        }
      }

      checkAndSetScrollable() {
        if (!this.useCustomPagination || !this.sliderControls) return;

        const paginationContainer = this.sliderControls.querySelector('.swiper-pagination-custom-main');
        if (!paginationContainer) return;

        const containerScrollWidth = paginationContainer.scrollWidth;
        const containerClientWidth = paginationContainer.clientWidth;

        if (containerScrollWidth > containerClientWidth) {
          paginationContainer.classList.add('will-scroll');
        } else {
          paginationContainer.classList.remove('will-scroll');
        }
      }

      scrollToActivePagination(activeIndex) {
        if (!this.useCustomPagination || !this.sliderControls) return;

        const paginationContainer = this.sliderControls.querySelector('.swiper-pagination-custom-main');
        if (!paginationContainer) return;

        const paginationItems = paginationContainer.querySelectorAll('.swiper-pagination-bullet');
        if (!paginationItems.length) return;

        const activeItem = paginationItems[activeIndex];
        if (!activeItem) return;

        // 检查容器是否需要滚动
        const containerScrollWidth = paginationContainer.scrollWidth;
        const containerClientWidth = paginationContainer.clientWidth;

        // 如果内容不超出容器宽度，不需要滚动
        if (containerScrollWidth <= containerClientWidth) return;

        // 清除之前的定时器，避免重复执行
        if (this.scrollTimeout) {
          clearTimeout(this.scrollTimeout);
        }

        // 延迟执行滚动，等待 Swiper 切换动画完成
        this.scrollTimeout = setTimeout(() => {
          const containerRect = paginationContainer.getBoundingClientRect();
          const activeItemRect = activeItem.getBoundingClientRect();
          const containerLeft = containerRect.left;
          const activeItemLeft = activeItemRect.left - containerLeft + paginationContainer.scrollLeft;
          const activeItemWidth = activeItemRect.width;

          const targetScrollLeft = activeItemLeft - (containerClientWidth / 2) + (activeItemWidth / 2);
          const maxScrollLeft = containerScrollWidth - containerClientWidth;
          const finalScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

          paginationContainer.scrollTo({
            left: finalScrollLeft,
            behavior: 'smooth'
          });
        }, 100);
      }

      destroyZoom() {
        this.cleanupScrollAnimations.forEach((fn) => fn());
        this.cleanupScrollAnimations = [];
        this.style.clipPath = 'none';
        if (this.sliderControls) this.sliderControls.style.bottom = 0;
      }
    }
  );
}